import { RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type UploadPhase = 'compressing' | 'preparing' | 'uploading' | 'confirming';

interface UploadProgressProps {
  fileName: string;
  progress: number;
  phase: UploadPhase | null;
  error: string | null;
  onCancel: () => void;
  onRetry: () => void;
}

export function UploadProgress({ fileName, progress, phase, error, onCancel, onRetry }: UploadProgressProps) {
  const { t } = useTranslation();

  const phaseLabel = phase ? t(`documents.upload.phases.${phase}`) : '';
  const isIndeterminate = phase === 'compressing' || phase === 'preparing' || phase === 'confirming';

  return (
    <div className='flex flex-col items-center gap-4 py-6'>
      {error ? (
        <>
          <div className='w-full bg-red-50 border border-red-200 rounded-lg p-4 text-center'>
            <p className='text-sm text-red-700'>{error}</p>
          </div>
          <div className='flex gap-3 w-full'>
            <button
              type='button'
              onClick={onRetry}
              className='flex items-center justify-center gap-2 flex-1 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition'>
              <RefreshCw size={16} />
              {t('documents.upload.retry')}
            </button>
            <button
              type='button'
              onClick={onCancel}
              className='flex items-center justify-center gap-2 flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition'>
              {t('common.cancel')}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className='text-sm text-gray-600 font-medium truncate max-w-full'>
            {t('documents.upload.progress', { fileName })}
          </p>

          {/* Progress bar */}
          <div className='w-full'>
            <div className='w-full bg-gray-200 rounded-full h-2.5 overflow-hidden'>
              {isIndeterminate ? (
                <div className='h-2.5 rounded-full bg-primary w-1/3 animate-[indeterminate_1.5s_ease-in-out_infinite]' />
              ) : (
                <div
                  className='h-2.5 rounded-full bg-primary transition-all duration-300'
                  style={{ width: `${progress}%` }}
                />
              )}
            </div>
            <p className='text-xs text-gray-500 text-center mt-2'>
              {isIndeterminate ? phaseLabel : t('documents.upload.percentage', { percent: progress })}
            </p>
          </div>

          {/* Cancel button */}
          <button
            type='button'
            onClick={onCancel}
            className='flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition'>
            <X size={14} />
            {t('documents.upload.cancel')}
          </button>
        </>
      )}
    </div>
  );
}
